import moment from "moment";

/**
 * Returns the "After program ends" payout moment: the program end date plus offset days.
 * Returns null when the end date is missing/invalid or the offset isn't a finite number.
 */
export function getPayoutDate(endDate, offsetDays) {
  if (!endDate || !Number.isFinite(offsetDays)) return null;
  const end = moment(endDate);
  return end.isValid() ? end.add(offsetDays, "days") : null;
}

/**
 * The margin a reward cutoff must clear its payout date (or, with no payout date,
 * the program end date) by.
 *
 * The payout sweep runs every 5 minutes and selects a program only while now is
 * between its due instant and its cutoff. Both of those bounds carry the payout
 * offset, so the offset cancels and the window's length is exactly
 * `ruleCalculationWindow - endDate`. A cutoff merely *after* the boundary is not
 * enough: a window shorter than one tick can fall whole between two sweeps and the
 * program never pays, with nothing logged. One interval is the arithmetic minimum
 * and this is three, because a sweep run can fail or be truncated by its attempt
 * deadline, and a window with a single chance is one missed run from that silence.
 *
 * ADO 192799.
 */
export const MIN_REWARD_CUTOFF_MINUTES = 15;

/**
 * The earliest reward cutoff that leaves the sweep a window it can land in, measured
 * from `base` — the payout date when there is one, otherwise the program end date.
 * Returns null when `base` is missing or invalid, so callers can defer to whichever
 * rule owns that error.
 */
export function getRewardCutoffFloor(base) {
  if (!base) return null;
  const m = moment(base);
  return m.isValid() ? m.add(MIN_REWARD_CUTOFF_MINUTES, "minutes") : null;
}

/**
 * Whether `cutoff` clears the floor measured from `base`. True when either is
 * missing, for the same reason `getRewardCutoffFloor` returns null.
 *
 * Both builder steps ask this same question from opposite ends — the timeline step
 * validates the cutoff against a fixed payout date, the reward step validates the
 * payout offset against a fixed cutoff — so it lives here rather than in either.
 */
export function clearsRewardCutoffFloor(cutoff, base) {
  const floor = getRewardCutoffFloor(base);
  if (!floor || !cutoff) return true;
  const m = moment(cutoff);
  return !m.isValid() || !m.isBefore(floor);
}
